import { useState, useMemo, useEffect } from 'react';
import {
  Inbox,
  Sun,
  Clock,
  Calendar,
  Archive,
  CheckCircle2,
  Plus,
  ChevronDown
} from 'lucide-react';
import EmptyState from '../components/common/EmptyState';
import Section from '../components/Section';
import TaskItem from '../components/TaskItem';
import TaskModal from '../components/TaskModal';
import ProjectModal from '../components/ProjectModal';
import GoalModal from '../components/GoalModal';
import LinkModal from '../components/LinkModal';
import OneOnOnePickerModal from '../components/OneOnOnePickerModal';
import ConfirmModal from '../components/ConfirmModal';
import { InlineDatePicker } from '../components/quick-actions';
import { useTasks, TASK_STATUSES } from '../hooks/useTasks';
import { useTimeBlocks } from '../hooks/useTimeBlocks';
import { useProjects } from '../hooks/useProjects';
import { useGoals } from '../hooks/useGoals';
import { useLinks } from '../hooks/useLinks';
import { useAuth } from '../hooks/useAuth';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useActionItems } from '../hooks/useActionItems';
import { getWeekStartForMeetingDay } from '../hooks/useOneOnOnes';
import { getLocalDateString } from '../utils/dateUtils';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';

// Note: getInternalUserName is no longer needed - hooks now use Firebase UID from useAuth

const VIEW_OPTIONS = [
  { id: 'today', label: 'Today', icon: Sun, description: 'Tasks for today' },
  { id: 'inbox', label: 'Inbox', icon: Inbox, description: 'Unclarified items' },
  { id: 'waiting', label: 'Waiting', icon: Clock, description: 'Blocked on someone' },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar, description: 'Future dates' },
  { id: 'someday', label: 'Someday', icon: Archive, description: 'Maybe later' },
  { id: 'done', label: 'Completed', icon: CheckCircle2, description: 'Finished tasks' }
];

export default function TasksPage({ onAddTask, initialView = 'today' }) {
  const { user, isAdmin } = useAuth();

  const {
    tasks,
    loading,
    addTask,
    updateTask,
    deleteTask,
    completeTask,
    uncompleteTask,
    addToToday,
    removeFromToday,
    setFollowUp,
    scheduleTask,
    moveToWaiting,
    moveToSomeday,
    getInboxTasks,
    getWaitingTasks,
    getScheduledTasks,
    getSomedayTasks,
    getCompletedTasks,
    getTodayTasks,
    getOverdueTasks,
    getUniqueDelegates
  } = useTasks();

  const { timeBlocks } = useTimeBlocks();
  const { projects, addProject, updateProject, deleteProject, getActiveProjects } = useProjects();
  const { goals, addGoal } = useGoals();
  const { addLink } = useLinks();

  // Get action item follow-ups for today
  const { getTodayFollowUps, toggleCompleted: toggleActionItemCompleted } = useActionItems();

  // Google Calendar integration
  const {
    isSignedIn: gcalSignedIn,
    getTimeBlocksForDate,
    createTimeBlock,
    TIME_BLOCK_NAMES
  } = useGoogleCalendar();

  const [activeView, setActiveView] = useState(initialView);

  // Sync activeView when initialView prop changes (e.g., switching between Inbox and Tasks tabs)
  useEffect(() => {
    setActiveView(initialView);
  }, [initialView]);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showOneOnOnePickerModal, setShowOneOnOnePickerModal] = useState(false);
  const [convertingTask, setConvertingTask] = useState(null);
  const [currentInboxItem, setCurrentInboxItem] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, task: null });
  const [delegateFilter, setDelegateFilter] = useState(null);
  const [quickAddValue, setQuickAddValue] = useState('');
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [followUpTask, setFollowUpTask] = useState(null); // For follow-up date picker

  // Get unique delegates for autocomplete and filtering
  // getUniqueDelegates() returns objects { name, uid, email, count }
  // Extract just names for backwards compatibility with DelegateInput
  const uniqueDelegateObjects = useMemo(() => getUniqueDelegates(), [getUniqueDelegates]);
  const uniqueDelegates = useMemo(() =>
    uniqueDelegateObjects.map(d => d.name),
    [uniqueDelegateObjects]
  );

  // Action item follow-ups for today
  const actionItemFollowUps = useMemo(() => {
    return getTodayFollowUps();
  }, [getTodayFollowUps]);

  // Today view sections (special handling)
  const todaySections = useMemo(() => {
    if (activeView !== 'today') return null;
    return getTodayTasks(); // Returns { overdue, today, followUps }
  }, [activeView, getTodayTasks]);

  // Deduplicate follow-ups: exclude tasks already shown in overdue or today sections
  const shownTaskIds = useMemo(() => {
    if (!todaySections) return new Set();
    return new Set([
      ...todaySections.overdue.map(t => t.id),
      ...todaySections.today.map(t => t.id)
    ]);
  }, [todaySections]);

  const uniqueFollowUps = useMemo(() => {
    if (!todaySections) return [];
    return todaySections.followUps.filter(t => !shownTaskIds.has(t.id));
  }, [todaySections, shownTaskIds]);

  // Filtered tasks based on view
  const viewTasks = useMemo(() => {
    let filtered = [];

    switch (activeView) {
      case 'today':
        // For today view, we use todaySections instead
        // Return flat list for backwards compatibility with count
        const sections = getTodayTasks();
        filtered = [...sections.overdue, ...sections.today, ...sections.followUps];
        break;
      case 'inbox':
        filtered = getInboxTasks();
        break;
      case 'waiting':
        filtered = getWaitingTasks();
        break;
      case 'scheduled':
        filtered = getScheduledTasks();
        break;
      case 'someday':
        filtered = getSomedayTasks();
        break;
      case 'done':
        filtered = getCompletedTasks().slice(0, 50); // Limit completed
        break;
      default:
        const defaultSections = getTodayTasks();
        filtered = [...defaultSections.overdue, ...defaultSections.today, ...defaultSections.followUps];
    }

    // Apply delegate filter (only in waiting view)
    if (delegateFilter && activeView === 'waiting') {
      filtered = filtered.filter(t => t.delegatedTo === delegateFilter);
    }

    return filtered;
  }, [activeView, delegateFilter, tasks]);

  const overdueTasks = useMemo(() => getOverdueTasks(), [tasks]);
  const inboxCount = useMemo(() => getInboxTasks().length, [tasks]);

  const handleAddTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    if (editingTask) {
      await updateTask(editingTask.id, taskData);
    } else {
      await addTask(taskData);
    }
  };

  const handleToggleComplete = async (task) => {
    if (task.completedAt) {
      await uncompleteTask(task.id);
    } else {
      await completeTask(task.id);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.task) {
      await deleteTask(deleteConfirm.task.id);
    }
    setDeleteConfirm({ show: false, task: null });
  };

  // Convert inbox item to project
  const handleConvertToProject = (task) => {
    setConvertingTask(task);
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData) => {
    // Create the project (userId is set automatically by the hook)
    const projectId = await addProject({
      ...projectData,
      // Carry over source info from the task
      source: convertingTask?.source || currentInboxItem?.source,
      sourceChannel: convertingTask?.sourceChannel || currentInboxItem?.sourceChannel,
      sourceLink: convertingTask?.sourceLink || currentInboxItem?.sourceLink
    });

    // Delete the original inbox task
    if (convertingTask) {
      await deleteTask(convertingTask.id);
    }
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
    }

    setConvertingTask(null);
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  // Inbox quick action handlers
  const handleInboxCreateProject = (task) => {
    setCurrentInboxItem(task);
    setPrefillData({
      name: task.title,
      description: task.notes || ''
    });
    setShowProjectModal(true);
  };

  const handleInboxAddAsGoal = (task) => {
    setCurrentInboxItem(task);
    setPrefillData({
      title: task.title,
      description: task.notes || ''
    });
    setShowGoalModal(true);
  };

  const handleSaveGoal = async (goalData) => {
    await addGoal(goalData);
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleInboxAddAsHabit = (task) => {
    setCurrentInboxItem(task);
    setPrefillData({
      name: task.title,
      url: task.sourceLink || ''
    });
    setShowLinkModal(true);
  };

  const handleSaveHabit = async (linkData) => {
    await addLink(linkData);
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleInboxAddToOneOnOne = (task) => {
    setCurrentInboxItem(task);
    setShowOneOnOnePickerModal(true);
  };

  const handleOneOnOneSelect = async ({ relationship, type }) => {
    if (!currentInboxItem || !relationship) return;

    try {
      const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);
      const participants = relationship.participants || [];

      // Find the session for this week - include participants constraint for security rules
      const sessionsQuery = query(
        collection(db, 'oneOnOnes'),
        where('participants', 'array-contains', user.uid),
        where('relationshipId', '==', relationship.id),
        where('weekStart', '==', weekStart)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      let sessionId;
      if (sessionsSnapshot.empty) {
        const newSession = {
          weekStart,
          relationshipId: relationship.id,
          participants: participants,
          status: 'active',
          createdAt: new Date()
        };
        const docRef = await addDoc(collection(db, 'oneOnOnes'), newSession);
        sessionId = docRef.id;
      } else {
        sessionId = sessionsSnapshot.docs[0].id;
      }

      if (type === 'topic') {
        await addDoc(collection(db, 'topics'), {
          text: currentInboxItem.title,
          addedByUid: user.uid, // Multi-tenant: use Firebase UID
          sessionId,
          participants: participants,
          discussed: false,
          createdAt: new Date()
        });
      } else if (type === 'actionItem') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 5 + (7 - dayOfWeek);
        const friday = new Date(today);
        friday.setDate(today.getDate() + daysUntilFriday);

        await addDoc(collection(db, 'actionItems'), {
          text: currentInboxItem.title,
          assignedToUid: user.uid, // Multi-tenant: use Firebase UID
          createdByUid: user.uid,
          dueDate: getLocalDateString(friday),
          sessionId,
          participants: participants,
          completed: false,
          createdAt: new Date()
        });
      }

      await deleteTask(currentInboxItem.id);
    } catch (error) {
      console.error('Error adding to 1:1:', error);
    }

    setCurrentInboxItem(null);
    setShowOneOnOnePickerModal(false);
  };

  // Expose addTask handler to parent
  if (onAddTask) {
    onAddTask.current = handleAddTask;
  }

  const activeViewConfig = VIEW_OPTIONS.find(v => v.id === activeView);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-text-tertiary">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4">
        {/* View Selector */}
        <div className="relative">
          <button
            onClick={() => setShowViewDropdown(!showViewDropdown)}
            className="flex items-center gap-2 px-3 py-2 bg-surface-card border border-border rounded-lg hover:border-border-strong transition-all cursor-pointer"
          >
            {activeViewConfig && (
              <>
                <activeViewConfig.icon size={16} className="text-accent" />
                <span className="text-sm font-medium text-text-primary">{activeViewConfig.label}</span>
              </>
            )}
            <ChevronDown size={14} className="text-text-tertiary" />
          </button>

          {showViewDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-10 min-w-[200px] overflow-hidden">
              {VIEW_OPTIONS.map((view) => (
                <button
                  key={view.id}
                  onClick={() => {
                    setActiveView(view.id);
                    setShowViewDropdown(false);
                    // Clear delegate filter when leaving waiting view
                    if (view.id !== 'waiting') setDelegateFilter(null);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer border-none transition-all ${
                    activeView === view.id
                      ? 'bg-accent-muted text-accent'
                      : 'bg-transparent text-text-secondary hover:bg-surface-hover'
                  }`}
                >
                  <view.icon size={16} />
                  <div>
                    <span className="text-xs font-medium block">{view.label}</span>
                    <span className="text-[10px] text-text-tertiary">{view.description}</span>
                  </div>
                  {view.id === 'inbox' && inboxCount > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 bg-accent text-surface-base text-[10px] font-semibold rounded">
                      {inboxCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick Add Input */}
        {activeView !== 'done' && (
          <div className="flex items-center gap-1.5 bg-surface-card border border-border rounded-lg px-2 py-1.5">
            <input
              type="text"
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.target.value)}
              placeholder={`Quick add to ${activeView === 'waiting' || activeView === 'scheduled' ? 'inbox' : activeView}...`}
              className="w-40 lg:w-56 bg-transparent border-none outline-none text-xs text-text-primary placeholder:text-text-muted"
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && quickAddValue.trim()) {
                  // Map view to status (waiting/scheduled need extra info, so add to inbox)
                  const statusMap = {
                    inbox: TASK_STATUSES.INBOX,
                    today: TASK_STATUSES.TODAY,
                    someday: TASK_STATUSES.SOMEDAY,
                    waiting: TASK_STATUSES.INBOX,
                    scheduled: TASK_STATUSES.INBOX,
                  };
                  await addTask({
                    title: quickAddValue.trim(),
                    status: statusMap[activeView] || TASK_STATUSES.INBOX
                  });
                  setQuickAddValue('');
                }
              }}
            />
            <button
              onClick={async () => {
                if (quickAddValue.trim()) {
                  const statusMap = {
                    inbox: TASK_STATUSES.INBOX,
                    today: TASK_STATUSES.TODAY,
                    someday: TASK_STATUSES.SOMEDAY,
                    waiting: TASK_STATUSES.INBOX,
                    scheduled: TASK_STATUSES.INBOX,
                  };
                  await addTask({
                    title: quickAddValue.trim(),
                    status: statusMap[activeView] || TASK_STATUSES.INBOX
                  });
                  setQuickAddValue('');
                }
              }}
              disabled={!quickAddValue.trim()}
              className="p-1.5 bg-accent text-white rounded cursor-pointer border-none hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Delegate Filter Chips (only in Waiting view) */}
      {activeView === 'waiting' && uniqueDelegates.length > 0 && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          <span className="text-[10px] text-text-muted flex-shrink-0">Filter by:</span>
          <button
            onClick={() => setDelegateFilter(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer border transition-all flex-shrink-0 ${
              delegateFilter === null
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-surface-card text-text-secondary border-border hover:border-amber-500/50'
            }`}
          >
            All
          </button>
          {uniqueDelegates.map((delegate) => (
            <button
              key={delegate}
              onClick={() => setDelegateFilter(delegateFilter === delegate ? null : delegate)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer border transition-all flex-shrink-0 ${
                delegateFilter === delegate
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-surface-card text-text-secondary border-border hover:border-amber-500/50'
              }`}
            >
              {delegate}
            </button>
          ))}
        </div>
      )}

      {/* Overdue Warning */}
      {overdueTasks.length > 0 && activeView !== 'done' && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-xs text-red-400 font-medium">
            {overdueTasks.length} overdue task{overdueTasks.length > 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Inbox Banner */}
      {inboxCount > 0 && activeView !== 'inbox' && (
        <button
          onClick={() => { setActiveView('inbox'); setDelegateFilter(null); }}
          className="w-full mb-4 p-3 bg-accent-muted border border-accent/30 rounded-lg flex items-center justify-between cursor-pointer hover:bg-accent-muted/80 transition-all"
        >
          <div className="flex items-center gap-2">
            <Inbox size={16} className="text-accent" />
            <span className="text-xs text-text-primary font-medium">
              {inboxCount} item{inboxCount > 1 ? 's' : ''} in Inbox
            </span>
          </div>
          <span className="text-xs text-accent">Process →</span>
        </button>
      )}

      {/* Task List */}
      <div className="space-y-2">
          {/* Today View with Sections */}
          {activeView === 'today' && todaySections ? (
            <>
              {/* Overdue Section */}
              {todaySections.overdue.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-xs font-semibold text-red-400 uppercase tracking-wide">
                      Overdue ({todaySections.overdue.length})
                    </span>
                  </div>
                  <div className="space-y-2 pl-4 border-l-2 border-red-500/30">
                    {todaySections.overdue.map((task) => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onToggleComplete={() => handleToggleComplete(task)}
                        onEdit={() => handleEditTask(task)}
                        onDelete={() => setDeleteConfirm({ show: true, task })}
                        onAddToToday={() => addToToday(task.id)}
                        onRemoveFromToday={() => removeFromToday(task.id)}
                        onSchedule={(taskId, date) => scheduleTask(taskId, date)}
                        onMoveToWaiting={moveToWaiting}
                        onMoveToSomeday={moveToSomeday}
                        useOverflowMenu={true}
                        showStatus={true}
                        isAdmin={isAdmin}
                        isOverdueSection={true}
                        delegates={uniqueDelegates}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Today's Tasks Section */}
              {todaySections.today.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <Sun size={14} className="text-accent" />
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                      Today's Tasks ({todaySections.today.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {todaySections.today.map((task) => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onToggleComplete={() => handleToggleComplete(task)}
                        onEdit={() => handleEditTask(task)}
                        onDelete={() => setDeleteConfirm({ show: true, task })}
                        onAddToToday={() => addToToday(task.id)}
                        onRemoveFromToday={() => removeFromToday(task.id)}
                        onSchedule={(taskId, date) => scheduleTask(taskId, date)}
                        onMoveToWaiting={moveToWaiting}
                        onMoveToSomeday={moveToSomeday}
                        useOverflowMenu={true}
                        showStatus={true}
                        isAdmin={isAdmin}
                        delegates={uniqueDelegates}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Follow-ups Section (deduplicated - excludes tasks already in overdue/today) */}
              {(uniqueFollowUps.length > 0 || actionItemFollowUps.length > 0) && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">
                      Follow-ups ({uniqueFollowUps.length + actionItemFollowUps.length})
                    </span>
                  </div>
                  <div className="space-y-2 pl-4 border-l-2 border-amber-500/30">
                    {/* Task follow-ups (deduplicated) */}
                    {uniqueFollowUps.map((task) => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onToggleComplete={() => handleToggleComplete(task)}
                        onEdit={() => handleEditTask(task)}
                        onDelete={() => setDeleteConfirm({ show: true, task })}
                        onSetFollowUp={() => setFollowUpTask(task)}
                        onSchedule={(taskId, date) => scheduleTask(taskId, date)}
                        onMoveToWaiting={moveToWaiting}
                        onMoveToSomeday={moveToSomeday}
                        useOverflowMenu={true}
                        showStatus={true}
                        isAdmin={isAdmin}
                        isFollowUp={true}
                        delegates={uniqueDelegates}
                      />
                    ))}

                    {/* Action item follow-ups from 1:1s */}
                    {actionItemFollowUps.map((item) => (
                      <div
                        key={`action-${item.id}`}
                        className="flex items-start gap-2 p-2.5 bg-surface-card rounded-lg border border-amber-500/30"
                      >
                        <button
                          onClick={() => toggleActionItemCompleted(item.id, item.completed)}
                          className="mt-0.5 w-4 h-4 rounded border border-border flex items-center justify-center cursor-pointer bg-transparent hover:border-accent transition-all flex-shrink-0"
                        >
                          {item.completed && <span className="text-accent text-[10px]">✓</span>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-text-primary">{item.text}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-medium">
                              1:1 Action Item
                            </span>
                            <span className="text-[10px] text-text-tertiary">
                              Assigned to {item.assignedTo}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state for Today */}
              {todaySections.overdue.length === 0 &&
               todaySections.today.length === 0 &&
               uniqueFollowUps.length === 0 &&
               actionItemFollowUps.length === 0 && (
                <EmptyState
                  type="today"
                  onNavigate={(view) => { setActiveView(view); setDelegateFilter(null); }}
                  onAction={(action) => {
                    if (action === 'addTask') handleAddTask();
                  }}
                />
              )}
            </>
          ) : viewTasks.length === 0 ? (
            <EmptyState
              type={activeView === 'done' ? 'inbox' : activeView}
              customTitle={activeView === 'done' ? 'No Completed Tasks' : undefined}
              customSubtitle={activeView === 'done' ? 'Complete tasks to see them here.' : undefined}
              showTip={activeView !== 'done'}
              onAction={(action) => {
                if (action === 'addTask') handleAddTask();
              }}
              onNavigate={(view) => { setActiveView(view); setDelegateFilter(null); }}
            />
          ) : (
            viewTasks.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                onToggleComplete={() => handleToggleComplete(task)}
                onEdit={() => handleEditTask(task)}
                onDelete={() => setDeleteConfirm({ show: true, task })}
                onAddToToday={() => addToToday(task.id)}
                onRemoveFromToday={() => removeFromToday(task.id)}
                onSchedule={(taskId, date) => scheduleTask(taskId, date)}
                onMoveToWaiting={moveToWaiting}
                onMoveToSomeday={moveToSomeday}
                // Use overflow menu for Waiting and Scheduled views
                useOverflowMenu={activeView === 'waiting' || activeView === 'scheduled'}
                showTodayInMenu={activeView === 'waiting' || activeView === 'scheduled'}
                hideWaitingInMenu={activeView === 'waiting'}
                hideScheduleInMenu={activeView === 'scheduled'}
                // Use full buttons for Inbox, Someday, Done
                showQuickActions={activeView === 'inbox' || activeView === 'someday'}
                showConversionActions={activeView === 'inbox'}
                showStatus={activeView === 'today'}
                showContext={activeView !== 'inbox'}
                isAdmin={isAdmin}
                onCreateProject={handleInboxCreateProject}
                onAddToOneOnOne={handleInboxAddToOneOnOne}
                onAddAsGoal={handleInboxAddAsGoal}
                onAddAsHabit={handleInboxAddAsHabit}
                delegates={uniqueDelegates}
              />
            ))
          )}
      </div>

      {/* Modals */}
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSave={handleSaveTask}
        editItem={editingTask}
        timeBlocks={timeBlocks}
        projects={projects}
        goals={goals}
        delegates={uniqueDelegates}
        gcalSignedIn={gcalSignedIn}
        getTimeBlocksForDate={getTimeBlocksForDate}
        createTimeBlock={createTimeBlock}
        timeBlockNames={TIME_BLOCK_NAMES}
        getTasksForBlock={(blockId) => tasks.filter(t => t.gcalTimeBlockId === blockId)}
      />

      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setConvertingTask(null);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveProject}
        editItem={prefillData || (convertingTask ? { name: convertingTask.title, description: convertingTask.notes } : null)}
        goals={goals}
      />

      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, task: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteConfirm.task?.title}"? This action cannot be undone.`}
      />

      <GoalModal
        isOpen={showGoalModal}
        onClose={() => {
          setShowGoalModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveGoal}
        editItem={prefillData}
      />

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveHabit}
        editItem={prefillData}
      />

      <OneOnOnePickerModal
        isOpen={showOneOnOnePickerModal}
        onClose={() => {
          setShowOneOnOnePickerModal(false);
          setCurrentInboxItem(null);
        }}
        onSelect={handleOneOnOneSelect}
        itemTitle={currentInboxItem?.title}
      />

      {/* Follow-up date picker modal */}
      {followUpTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFollowUpTask(null)}>
          <div className="bg-surface-card rounded-lg p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-medium text-text-primary mb-3">Reschedule Follow-up</h3>
            <p className="text-xs text-text-secondary mb-3">
              Task: {followUpTask.title}
            </p>
            <InlineDatePicker
              onConfirm={(date) => {
                setFollowUp(followUpTask.id, date);
                setFollowUpTask(null);
              }}
              onCancel={() => setFollowUpTask(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
